import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PwaIcon from "../components/PwaIcon";
import { useT } from "../i18n";
import { KeyRound } from "lucide-react";

const INVITE_KEY = "pantry_pending_invite";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function handleGoogleLogin(inviteCode) {
  // Persist invite so it survives the OAuth roundtrip.
  if (inviteCode) {
    try { localStorage.setItem(INVITE_KEY, inviteCode.toUpperCase()); } catch (_e) { /* noop */ }
  }
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Landing() {
  const [invite, setInvite] = useState("");
  const { t } = useT();

  useEffect(() => {
    // Deep-link support: /?invite=ABC123
    const params = new URLSearchParams(window.location.search);
    const q = params.get("invite");
    if (q && /^[A-Z0-9]{4,8}$/i.test(q.trim())) {
      const code = q.trim().toUpperCase();
      setInvite(code);
      try { localStorage.setItem(INVITE_KEY, code); } catch (_e) { /* noop */ }
    } else {
      // Prefill from any previously stored invite (e.g. re-open Landing)
      try {
        const stored = localStorage.getItem(INVITE_KEY);
        if (stored) setInvite(stored);
      } catch (_e) { /* noop */ }
    }
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="landing-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/8725754/pexels-photo-8725754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
      />
      <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px]" />

      <div className="relative flex-1 flex flex-col px-7 pt-safe pt-16">
        <div className="flex items-center gap-3">
          <PwaIcon size={40} />
          <div>
            <div className="font-heading text-lg font-semibold tracking-tight leading-none">Niper</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">Nihil Perditum</div>
          </div>
        </div>

        <div className="mt-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fresh-green-soft text-fresh-green-text text-[11px] font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-fresh-green" /> {t("landing.fresh_tracker")}
          </div>
          <h1 className="mt-5 font-heading text-5xl leading-[1.05] tracking-tight font-medium text-zinc-950">
            {t("landing.hero.line1")}<br />
            <span className="text-fresh-red">{t("landing.hero.line2_red")}</span>{" "}
            <span className="text-fresh-yellow">{t("landing.hero.line2_yellow")}</span>{" "}
            <span className="text-fresh-green">{t("landing.hero.line2_green")}</span>
          </h1>
          <p className="mt-5 text-zinc-600 text-base leading-relaxed max-w-xs">
            {t("landing.subtitle")}
          </p>
        </div>

        <div className="mt-auto mb-10 pt-10">
          <Button
            onClick={() => handleGoogleLogin(invite)}
            className="w-full h-14 rounded-2xl bg-zinc-950 hover:bg-zinc-800 text-white text-base font-medium"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            {invite ? t("common.signin_join", { code: invite }) : t("common.signin_google")}
          </Button>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-300/60" />
            <span className="text-[11px] tracking-widest uppercase text-zinc-500 font-bold">{t("landing.invite.divider")}</span>
            <div className="flex-1 h-px bg-zinc-300/60" />
          </div>

          <div className="mt-4 relative">
            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={invite}
              onChange={(e) => {
                const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
                setInvite(v);
                try {
                  if (v) localStorage.setItem(INVITE_KEY, v);
                  else localStorage.removeItem(INVITE_KEY);
                } catch (_e) { /* noop */ }
              }}
              placeholder={t("landing.invite.placeholder")}
              maxLength={8}
              className="h-12 pl-11 rounded-xl text-lg font-mono tracking-[0.3em] text-center bg-white/80 border-zinc-200 focus-visible:ring-zinc-950"
              data-testid="landing-invite-input"
            />
            {invite && (
              <p className="mt-2 text-[11px] text-zinc-500 text-center" data-testid="landing-invite-hint">
                {t("landing.invite.hint", { code: invite })}
              </p>
            )}
          </div>

          <p className="text-center text-[11px] text-zinc-500 mt-5 tracking-wide">
            {t("landing.privacy")}
          </p>
        </div>
      </div>
    </div>
  );
}
